import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";

const TITLE = "New Job? See Any Work Outfit On You Before You Buy — Like2Fit";
const DESC =
  "Share any work outfit you love and see exactly how it looks on your body — at your size and budget. Find what you already own, shop the rest.";
const URL = "https://like2fit.com/new-job";

export const Route = createFileRoute("/new-job")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "/icons/icon-512.png" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: NewJobPage,
});

const INK = "#0A0A0A";
const CORAL = "#FF5C5C";
const TINT = "#FAECE7";
const MUTED = "rgba(10,10,10,0.62)";
const SERIF = "'Instrument Serif', 'Times New Roman', Georgia, serif";
const SANS = "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

function preserveUtmAndGo(campaign: "new-job") {
  try {
    const params = new URLSearchParams(window.location.search);
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v) sessionStorage.setItem(`l2f_${k}`, v);
    }
    sessionStorage.setItem("l2f_campaign_entry", campaign);
  } catch {
    /* ignore storage errors */
  }
  window.location.href = "/";
}

function NewJobPage() {
  useEffect(() => {
    const id = "l2f-campaign-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Work+Sans:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);

  const onCTA = () => preserveUtmAndGo("new-job");

  return (
    <div style={{ minHeight: "100vh", background: "#fff", color: INK, fontFamily: SANS }}>
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px 24px",
          borderBottom: "0.5px solid rgba(10,10,10,0.05)",
        }}
      >
        <span style={{ color: CORAL, fontWeight: 600, fontSize: 20, letterSpacing: "-0.5px" }}>
          Like2Fit
        </span>
      </header>

      <main style={{ maxWidth: 560, margin: "0 auto", padding: "0 24px" }}>
        {/* Hero */}
        <section style={{ padding: "56px 0 48px" }}>
          <p
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.25em",
              color: CORAL,
              margin: "0 0 20px",
            }}
          >
            Like2Fit · New Job
          </p>
          <h1
            style={{
              fontFamily: SERIF,
              fontStyle: "italic",
              fontSize: 44,
              lineHeight: 1.05,
              letterSpacing: "-0.5px",
              margin: 0,
              color: INK,
            }}
          >
            New job? See the outfit on you before you buy
          </h1>
          <p style={{ marginTop: 22, fontSize: 16, lineHeight: 1.6, color: MUTED }}>
            Share any work outfit you love and see exactly how it looks on your body — at your
            size and budget. Find what you already own, shop the rest.
          </p>

          <button onClick={onCTA} style={primaryBtn}>
            Upload an outfit
          </button>
          <p style={disclaimer}>Free to try · No account needed</p>
        </section>

        {/* Three steps — duplicated from homepage block */}
        <section style={{ padding: "32px 0 56px" }}>
          <p style={eyebrow}>How it works</p>
          <h2 style={sectionHeading}>Three taps to a real answer.</h2>

          <div style={{ display: "flex", flexDirection: "column", gap: 28, marginTop: 32 }}>
            <Step
              n="01"
              icon="📤"
              title="Share any outfit"
              body="See something you love on TikTok, Instagram or Pinterest. Tap share and select Like2Fit."
            />
            <Step
              n="02"
              icon="👤"
              title="See it on your body"
              body="Your height, shape and proportions. See it on your body and discover different ways to wear it."
            />
            <Step
              n="03"
              icon="🛍️"
              title="Buy confidently"
              body="Every item at your size and budget. See what you already own. Only buy what you need."
            />
          </div>
        </section>

        {/* Final CTA */}
        <section
          style={{
            background: INK,
            color: "#fff",
            borderRadius: 24,
            padding: "56px 28px",
            textAlign: "center",
            margin: "0 0 56px",
          }}
        >
          <p
            style={{
              fontFamily: SERIF,
              fontStyle: "italic",
              fontSize: 32,
              lineHeight: 1.1,
              margin: "0 0 28px",
            }}
          >
            Walk in confident on day one.
          </p>
          <button onClick={onCTA} style={{ ...primaryBtn, marginTop: 0 }}>
            Upload an outfit
          </button>
          <p style={{ ...disclaimer, color: "rgba(255,255,255,0.6)" }}>
            Free to try · No sign up needed
          </p>
        </section>
      </main>

      <footer
        style={{
          padding: "32px 24px 48px",
          borderTop: "0.5px solid rgba(10,10,10,0.06)",
          textAlign: "center",
        }}
      >
        <p style={{ color: CORAL, fontWeight: 600, fontSize: 18, margin: "0 0 12px" }}>Like2Fit</p>
        <nav
          aria-label="Footer"
          style={{ display: "flex", gap: 18, justifyContent: "center", fontSize: 13 }}
        >
          <Link to="/privacy" style={footerLink}>
            Privacy
          </Link>
          <Link to="/terms" style={footerLink}>
            Terms
          </Link>
          <Link to="/" style={footerLink}>
            Back to home
          </Link>
        </nav>
      </footer>
    </div>
  );
}

function Step({ n, icon, title, body }: { n: string; icon: string; title: string; body: string }) {
  return (
    <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
      <div
        style={{
          flexShrink: 0,
          width: 56,
          height: 56,
          borderRadius: 16,
          background: TINT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 24,
        }}
        aria-hidden="true"
      >
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.2em",
            color: CORAL,
            margin: "0 0 6px",
          }}
        >
          {n}
        </p>
        <h3 style={{ fontSize: 19, fontWeight: 700, margin: "0 0 6px", color: INK }}>{title}</h3>
        <p style={{ fontSize: 15, lineHeight: 1.55, color: MUTED, margin: 0 }}>{body}</p>
      </div>
    </div>
  );
}

const primaryBtn: React.CSSProperties = {
  display: "block",
  width: "100%",
  marginTop: 32,
  padding: "18px 24px",
  background: CORAL,
  color: "#fff",
  border: "none",
  borderRadius: 999,
  fontSize: 16,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
  letterSpacing: "-0.1px",
};

const disclaimer: React.CSSProperties = {
  marginTop: 14,
  fontSize: 13,
  color: "rgba(10,10,10,0.5)",
  textAlign: "center",
};

const eyebrow: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.25em",
  color: CORAL,
  margin: "0 0 12px",
};

const sectionHeading: React.CSSProperties = {
  fontFamily: SERIF,
  fontStyle: "italic",
  fontSize: 36,
  lineHeight: 1.1,
  letterSpacing: "-0.3px",
  margin: 0,
  color: INK,
};

const footerLink: React.CSSProperties = {
  color: "rgba(10,10,10,0.7)",
  textDecoration: "none",
};
